<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { mdiContentSave } from '@mdi/js'
import { isEqual } from 'lodash'
import { getRoleName } from '@/shared/utils/user'
import { useUserStore } from '@/modules/users/stores/user.store'
import { useToaster } from '@/modules/toaster/composables/use-toaster'
import { DEFAULT_ACTIVATION_FORM, DEFAULT_PROFILE_FORM } from '../constants/user.constants'
import type { ActivationUser, UserProfile } from '@/modules/users/models/user.model'
import type { Validate } from '@/shared/types/validation.types'

import BaseModal from '@/shared/components/modal/BaseModal.vue'
import BodyModal from '@/shared/components/modal/BodyModal.vue'
import FooterModal from '@/shared/components/modal/FooterModal.vue'
import TextInput from '@/shared/components/input/TextInput.vue'
import PrimaryButton from '@/shared/components/button/PrimaryButton.vue'
import DefaultButton from '@/shared/components/button/DefaultButton.vue'
import HeaderModal from '@/shared/components/modal/HeaderModal.vue'
import TabUnderline from '@/shared/components/tab/TabUnderline.vue'
import TabContent from '@/shared/components/tab/TabContent.vue'
import ChangeLogMenu from '@/shared/components/log/ChangeLogMenu.vue'
import UserActivationForm from '@/shared/components/forms/UserActivationForm.vue'

interface Props {
  title: string
}

defineProps<Props>()

const open = defineModel<boolean>('open', { required: true })

const userStore = useUserStore()

const { success, error } = useToaster()

const isLogActive = ref<boolean>(false)
const isLoading = ref<boolean>(true)
const isSaving = ref<boolean>(false)
const validate = ref<{ [key: string]: Validate }>({})
const activationUser = ref<ActivationUser>({ ...DEFAULT_ACTIVATION_FORM })
const profile = ref<UserProfile>({ ...DEFAULT_PROFILE_FORM })
const original = ref<UserProfile>({ ...DEFAULT_PROFILE_FORM })
const tabIndex = ref<number>(0)

const initials = computed(
  (): string =>
    `${userStore.user ? userStore.user.firstName?.charAt(0) : ''}${userStore.user ? userStore.user.lastName?.charAt(0) : ''}`,
)

const isDirty = computed((): boolean => !isEqual(profile.value, original.value))

const roleName = computed((): string => getRoleName(userStore.user?.roleId))

const onClose = (): void => {
  validate.value = {}
  open.value = false
}

const updateProfile = async (): Promise<void> => {
  isSaving.value = true

  const isSuccess = await userStore.updateCurrentUserProfile({
    firstName: profile.value.firstName,
    lastName: profile.value.lastName,
    email: profile.value.email,
    phone: profile.value.phone,
  })

  isSaving.value = false

  if (isSuccess) {
    original.value = { ...profile.value }
    success('message.savedSuccess')
  } else {
    profile.value = { ...original.value }
    error('error.unexpected')
  }
}

const loadProfile = async (): Promise<void> => {
  const response = await userStore.getUserProfile()

  if (response.error || !response.data) return

  profile.value = { ...response.data }
  original.value = { ...response.data }
}

const loadActivation = async (): Promise<void> => {
  const response = await userStore.getUserActivation()

  if (response.error || !response.data) return

  activationUser.value = response.data
}

const onLoad = async (): Promise<void> => {
  try {
    await loadProfile()
    await loadActivation()
  } finally {
    isLoading.value = false
  }
}

watch(
  open,
  (newVal) => {
    if (newVal) {
      onLoad()
    }
  },
  { immediate: true },
)
</script>

<template>
  <BaseModal :open="open" :confirm="isDirty" @close="onClose">
    <HeaderModal :title="title" @close="onClose">
      <ChangeLogMenu
        v-model="isLogActive"
        :first-name="profile.firstName"
        :last-name="profile.lastName"
        :login-date="profile.lastUserActivityDate"
        :updated-by="profile.updateByUserFullname"
        :update-date="profile.updateDate"
        :created-by="profile.createdByUserFullname"
        :created-date="profile.createdDate"
        :loading="isLoading || isSaving"
      />
    </HeaderModal>
    <BodyModal class="min-h-101">
      <TabUnderline
        v-model="tabIndex"
        :items="[
          { label: $t('glossary.general'), disabled: isSaving },
          { label: $t('glossary.activation'), disabled: isSaving },
        ]"
      />
      <TabContent v-model="tabIndex" :index="0">
        <div class="flex flex-col gap-2">
          <div class="flex justify-center rounded-md py-3">
            <div
              class="flex items-center justify-center bg-gray-500 rounded-full w-32 h-32 shadow-md"
            >
              <span class="text-5xl text-white uppercase">{{ initials }}</span>
            </div>
          </div>
          <div class="flex gap-4">
            <div class="basis-1/2">
              <TextInput
                v-model="profile.firstName"
                :label="$t('glossary.firstName')"
                required
                :disabled="isSaving"
                @validate="validate.firstName = $event"
              />
            </div>
            <div class="basis-1/2">
              <TextInput
                v-model="profile.lastName"
                :label="$t('glossary.lastName')"
                required
                :disabled="isSaving"
                @validate="validate.lastName = $event"
              />
            </div>
          </div>
          <div class="flex gap-4">
            <div class="basis-1/2">
              <TextInput
                v-model="profile.email"
                type="email"
                :label="$t('glossary.contactEmail')"
                required
                :disabled="isSaving"
                @validate="validate.email = $event"
              />
            </div>
            <div class="basis-1/2">
              <TextInput
                v-model="profile.phone"
                :label="$t('glossary.contactPhone')"
                :disabled="isSaving"
              />
            </div>
          </div>
          <div class="flex gap-4">
            <div class="basis-1/2">
              <TextInput
                :label="$t('glossary.organization')"
                v-model="profile.organization"
                disabled
              />
            </div>
            <div class="basis-1/2">
              <TextInput :label="$t('glossary.role')" v-model="roleName" disabled />
            </div>
          </div>
        </div>
      </TabContent>
      <TabContent v-model="tabIndex" :index="1">
        <UserActivationForm :user="activationUser" />
      </TabContent>
    </BodyModal>
    <FooterModal class="justify-between">
      <DefaultButton
        :text="isDirty ? $t('common.cancel') : $t('common.close')"
        @click="onClose()"
      />
      <PrimaryButton
        v-model="validate"
        :text="$t('common.save')"
        :icon="mdiContentSave"
        :disabled="!isDirty"
        :loading="isSaving"
        class="ms-2"
        @click="updateProfile"
      />
    </FooterModal>
  </BaseModal>
</template>
